import {getSetting} from '@wc-stripe/utils';
import BaseGateway from './BaseGateway';
import LocalPaymentGateway from './LocalPaymentGateway';
import UniversalPaymentGateway from './UniversalPaymentGateway';
import {createCreditCardGateway} from './credit-card';

/**
 * CheckoutSessionGateway - Adaptive Pricing's shared Elements-with-Checkout-Sessions coordinator.
 *
 * Owns the single `checkout` instance (stripe.initCheckoutElementsSdk()) and the single shared payment
 * element Adaptive Pricing requires - a CheckoutSession has a 1:1 cardinality with its payment
 * element, so it can't be created per-gateway like the standard stripe.elements() model.
 *
 * Standalone gateways (stripe_cc, stripe_ideal, etc.) keep their own radio button/row unchanged.
 * CheckoutSessionController.onPaymentMethodSelected() calls onPaymentMethodSelected() below
 * directly on selection changes, which moves the one shared element between containers instead
 * of each gateway creating/mounting its own.
 *
 * @since 4.1.0
 */
class CheckoutSessionGateway extends BaseGateway {

    checkout = null;

    activeGatewayId = null;

    activeContainer = null;

    gatewayInstances = new Map();

    /**
     * this.id always reflects whichever gateway radio is currently checked, rather than a fixed
     * value set at construction - callers like BaseController.isPaymentMethodAvailable() (which
     * checks cart.isPaymentMethodAvailable(this.gateway.id)) then evaluate against the real,
     * currently-selected gateway instead of a fixed placeholder id.
     *
     * @returns {string}
     */
    get id() {
        return document.querySelector('input[name="payment_method"]:checked')?.value ?? this._id;
    }

    set id(value) {
        this._id = value;
    }

    /**
     * Resolves to the currently-selected gateway's own settings (elementOptions,
     * paymentMethodType, button_placement, etc.) rather than the checkoutSession payload passed
     * into the constructor - CheckoutSessionGateway stands in as `this.gateway` for the full
     * generic CheckoutController/BaseController lifecycle, so inherited, un-overridden BaseGateway
     * methods (canMakePayment(), getElementOptions(), getPaymentMethodTypes(), etc.) need this to
     * look like whatever gateway is actually active, not the checkoutSession's own
     * {client_secret, session_id} shape. Falls back to {} so BaseGateway's constructor (which
     * unconditionally reads this.settings.button_placement) never throws, even before a gateway is
     * selected. The checkoutSession payload itself is kept separately in this._settings - see
     * isActive()/setup() below, which need the real client_secret, not a per-gateway settings object.
     *
     * @returns {Object}
     */
    get settings() {
        return this.getGatewayInstance(this.id)?.settings ?? this._settings ?? {};
    }

    set settings(value) {
        this._settings = value;
    }

    /**
     * @returns {boolean}
     */
    isActive() {
        return !!this._settings?.client_secret;
    }

    onStripeSDKLoaded(stripe) {
        super.onStripeSDKLoaded(stripe);

        if (this.isActive()) {
            this.setup();
        }
    }

    onPaymentMethodAvailable() {

    }

    /**
     * Creates the shared checkout instance and payment element. Synchronous - unlike the old
     * (privately-gated) initCustomCheckout, initCheckoutElementsSdk() doesn't return a Promise.
     *
     * @returns {void}
     */
    setup() {
        this.checkout = this.stripe.initCheckoutElementsSdk({clientSecret: this._settings.client_secret});
        this.paymentElement = this.checkout.createPaymentElement(this.buildPaymentElementOptions());
    }

    /**
     * Overrides BaseGateway.buildPaymentElementOptions() - merges the currently-selected gateway's
     * own options (terms, billing-detail field suppression - reused from its bare instance via
     * getGatewayInstance()) with visiblePaymentMethods, so the shared element narrows to just that
     * gateway's payment method type. BaseGateway's own createPaymentElement()/updatePaymentElement()
     * (and the inherited checkout_field_changed listener that calls updatePaymentElement() on
     * billing-field changes) already call this generically, so overriding it here is enough for
     * those inherited mechanisms to work correctly with no extra wiring.
     *
     * defaultValues is stripped out - the Payment Element created from a Checkout Session
     * (checkout.createPaymentElement()) doesn't support it (Stripe's supported options are fields,
     * layout, paymentMethodOrder, readOnly, terms, wallets), unlike the standalone
     * stripe.elements()-created Payment Element every other gateway uses, where BaseGateway's
     * defaultValues is honored.
     *
     * @param {boolean} isUpdate
     * @returns {Object}
     */
    buildPaymentElementOptions(isUpdate = false) {
        const target = this.getGatewayTarget(this.id);
        const {
            defaultValues,
            ...childOptions
        } = this.getGatewayInstance(this.id)?.buildPaymentElementOptions(isUpdate) ?? {};

        return {
            ...childOptions,
            ...(target && {visiblePaymentMethods: [target.paymentMethodType]})
        };
    }

    /**
     * @param {string} gatewayId
     * @returns {void}
     */
    onPaymentMethodSelected(gatewayId) {
        this.id = gatewayId;
        if (!this.paymentElement) {
            return;
        }

        this.mountActiveGateway(gatewayId);
    }

    /**
     * Moves the shared payment element into the given (or currently-selected) gateway's
     * container, filtered down to just that gateway's payment method type. Called directly once
     * setup() resolves (so the initial page-load selection is handled without waiting on a
     * payment_method_selected event), and again from onPaymentMethodSelected() on every
     * subsequent change. If the gateway hasn't actually changed (e.g. re-triggered by
     * updated_checkout without a real selection change), only the options get refreshed - the
     * element isn't unmounted/remounted, which would risk losing in-progress input like a
     * partially-typed card number.
     *
     * @param {string} [gatewayId] - Defaults to the currently-selected gateway (this.id).
     * @returns {void}
     */
    mountActiveGateway(gatewayId = this.id) {
        const target = this.getGatewayTarget(gatewayId);
        if (!target) {
            return;
        }

        if (gatewayId === this.activeGatewayId) {
            this.paymentElement.update(this.buildPaymentElementOptions(true));
            return;
        }

        this.performMount(gatewayId, target);
    }

    /**
     * Physically (re)mounts the shared element into a gateway's container - always, regardless
     * of activeGatewayId. Needed because WooCommerce's own checkout re-render (on every
     * updated_checkout) replaces the payment-methods HTML wholesale, destroying the previously
     * mounted iframe even though the gateway selection itself hasn't changed - activeGatewayId
     * reflects *logical* selection, not whether the element is still physically in the DOM.
     * mountPaymentElement() below relies on this, since it's only called once
     * BaseGateway.shouldMountElement() has already confirmed the container needs a real mount.
     *
     * @param {string} gatewayId
     * @param {{selector: string, paymentMethodType: string}} target
     * @returns {void}
     */
    performMount(gatewayId, target) {
        if (this.activeContainer) {
            this.paymentElement.unmount();
        }

        // update() has to run after mount() has actually settled, not before - confirmed by
        // testing that calling it beforehand (in any order relative to unmount()) gets silently
        // dropped, leaving the element showing whatever visiblePaymentMethods it was originally
        // created with, and that a setTimeout delay after mount() does work. Using the 'ready'
        // event instead of a fixed delay - it's Stripe's own signal for "element has finished
        // rendering after mount," so it isn't a guess at how long that takes. Registered before
        // mount() is called so there's no race between the two.
        const onReady = () => {
            this.paymentElement.off('ready', onReady);
            this.paymentElement.update(this.buildPaymentElementOptions(true));
        };
        this.paymentElement.on('ready', onReady);

        this.paymentElement.mount(target.selector);

        this.activeGatewayId = gatewayId;
        this.activeContainer = target.selector;
    }

    /**
     * Reads the selector/paymentMethodType a standalone gateway localized via its own
     * get_payment_method_data() (see AbstractGateway::get_element_selector()). Returns null for
     * gateways that aren't Adaptive Pricing-eligible (see getGatewayInstance()), even if PHP
     * happens to localize a selector for them (e.g. stripe_cc in inline/custom form mode).
     *
     * @param {string} gatewayId
     * @returns {{selector: string, paymentMethodType: string}|null}
     */
    getGatewayTarget(gatewayId) {
        const instance = this.getGatewayInstance(gatewayId);
        if (!instance) {
            return null;
        }
        const settings = getSetting(`${gatewayId}_data`);
        const paymentMethodType = instance.getPaymentMethodType();
        if (!settings?.selector || !paymentMethodType) {
            return null;
        }
        return {selector: settings.selector, paymentMethodType};
    }

    /**
     * Lazily builds (and memoizes) a bare, controller-less gateway instance per payment method id,
     * purely so its inherited buildPaymentElementOptions() can be reused instead of duplicating
     * that settings-driven logic (terms display, billing-detail field suppression, defaultValues)
     * here. Never paired with a controller (setController()/initialize() are never called) since
     * buildPaymentElementOptions() only reads this.context/this.settings/DOM billing fields.
     *
     * Doubles as the single source of truth for Adaptive Pricing gateway eligibility - returns
     * null for anything not handled below (wallets/Link stay on their existing, unaffected path
     * per DESIGN.md's milestone-1 scope; stripe_cc only qualifies in 'payment' form mode, since
     * 'inline'/'custom' use a legacy Card Element, not a Payment Element).
     *
     * @param {string} gatewayId
     * @returns {BaseGateway|null}
     */
    getGatewayInstance(gatewayId) {
        // Lazily initialized here (in addition to the class field above) because this getter can
        // be invoked by the settings getter during BaseGateway's own constructor (super()), which
        // runs before this subclass's own field initializers have executed.
        if (!this.gatewayInstances) {
            this.gatewayInstances = new Map();
        }

        if (this.gatewayInstances.has(gatewayId)) {
            return this.gatewayInstances.get(gatewayId);
        }

        const data = getSetting('paymentMethods', {})[gatewayId];
        const settings = getSetting(`${gatewayId}_data`);
        let instance = null;

        if (data?.isLocal) {
            instance = new LocalPaymentGateway({id: gatewayId, settings, context: this.context});
        } else if (gatewayId === 'stripe_upm') {
            instance = new UniversalPaymentGateway({id: gatewayId, settings, context: this.context});
        } else if (gatewayId === 'stripe_cc' && settings?.cardFormType === 'payment') {
            instance = createCreditCardGateway('payment', {id: gatewayId, settings, context: this.context});
        }

        this.gatewayInstances.set(gatewayId, instance);
        return instance;
    }

    /**
     * Overrides BaseGateway.mountPaymentElement() - reachable via BaseController's generic
     * createPaymentElement() flow, called only once shouldMountElement() has already confirmed the
     * container has no mounted iframe (e.g. on updated_checkout, after WooCommerce's own re-render
     * wiped out the previous one). Always forces a real (re)mount via performMount() rather than
     * mountActiveGateway()'s activeGatewayId-based guard, which would otherwise wrongly skip
     * mounting here since the *logical* selection may not have changed even though the element's
     * DOM node is physically gone.
     *
     * @param {HTMLElement|NodeList} container - Unused; performMount() resolves its own container
     * from the currently-selected gateway's own selector.
     * @returns {Promise<Object>}
     */
    async mountPaymentElement(container) {
        const gatewayId = this.id;
        const target = this.getGatewayTarget(gatewayId);
        if (!target) {
            return;
        }

        this.performMount(gatewayId, target);
    }

    /**
     * Overrides BaseGateway.validatePaymentElement() - validates via the checkout instance's
     * actions instead of this.elements.submit(), since there's no per-gateway `elements` group
     * under Adaptive Pricing. Same contract as the base method: throws on error, marks
     * _elementsSubmitted on success.
     *
     * @returns {Promise<void>}
     */
    async validatePaymentElement() {
        try {
            const {actions: checkoutActions} = await this.checkout.loadActions();
            const response = await checkoutActions.validateElements();
            if (response.error) {
                throw response.error;
            }
            this._elementsSubmitted = true;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Overrides BaseGateway.confirmPayment() - same role as stripe.confirmPayment() plays for
     * every other gateway (called from BaseController.handleIntentConfirmation() once the WC
     * checkout AJAX response indicates a next action is required), but delegates to the
     * checkout instance's own confirm() instead, since the CheckoutSession already owns the
     * client_secret/payment method - there's no separate PaymentIntent client_secret to pass.
     *
     * @param {Object} data - Unused here (kept for signature parity with BaseGateway); the
     * checkout instance already holds its own session state.
     * @param {Object} options
     * @returns {Promise<Object>}
     */
    async confirmPayment(data, options = {}) {
        return this.confirmCheckoutSession(options);
    }

    /**
     * Overrides BaseGateway.confirmSetup() - see confirmPayment() above. checkout.confirm() isn't
     * intent-type-specific, so both share the same underlying call.
     *
     * @param {Object} data
     * @param {Object} options
     * @returns {Promise<Object>}
     */
    async confirmSetup(data, options = {}) {
        return this.confirmCheckoutSession(options);
    }

    /**
     * @param {Object} options
     * @returns {Promise<Object>}
     */
    async confirmCheckoutSession(options = {}) {
        const {actions: checkoutActions} = await this.checkout.loadActions();
        return checkoutActions.confirm(options);
    }

}

export default CheckoutSessionGateway;
export {CheckoutSessionGateway};